import '@lottiefiles/lottie-player'

import '@syncfusion/ej2-base/styles/material.css'
import '@syncfusion/ej2-popups/styles/material.css'
import '@syncfusion/ej2-react-calendars/styles/material.css'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MdOutlineAutoStories,
  MdOutlineMarkEmailRead,
  MdOutlineQuiz,
} from 'react-icons/md'
import { useHistory } from 'react-router'
import axiosClient from 'services/axios-client'
import styled from 'styled-components'
import { BookInfoContainer } from './style'

import { L10n, loadCldr } from '@syncfusion/ej2-base'
import { Skeleton } from 'antd'
import StyledTooltip from 'components/Elements/StyledTooltip'
import { useDashTour } from 'sections/Dashboard/hooks'
import { useParams } from 'react-router-dom'
import {
  useBookQuery,
  useBookQuestionsQuery,
  useBookRoleQuery,
  useBookStatsQuery,
} from 'store/queries'
import RecentActivity from '../RecentActivity'
import ThingsToDo from '../ThingsToDo'

/* eslint-disable */
loadCldr(
  require('cldr-data/main/de/ca-gregorian.json'),
  require('cldr-data/main/de/numbers.json'),
  require('cldr-data/main/de/timeZoneNames.json'),
  require('cldr-data/supplemental/numberingSystems.json'),
)
/* eslint-enable */

L10n.load({
  de: {
    calendar: { today: 'heute' },
  },
})

function BookInfo() {
  // TODO: Split current generic modal settings out into a generic page component to style the former modals.
  const { t } = useTranslation()
  const history = useHistory()
  const { bookId } = useParams()
  const launchTour = useDashTour()

  const { data: book } = useBookQuery(bookId)

  const { data: role } = useBookRoleQuery(bookId)
  const { data: questions } = useBookQuestionsQuery(bookId)
  const { data: stats } = useBookStatsQuery(bookId)

  const isEditorOrPurchaser =
    role?.name === 'Editor' || role?.name === 'Purchaser'

  // Run setup on first purchaser login.
  useEffect(() => {
    if (role?.name === 'Purchaser' && book?.should_run_setup) {
      axiosClient
        .put(`/book/${bookId}`, {
          should_run_setup: false,
        })
        .then(() => {
          history.push(`/dashboard/book/${bookId}/info/setup`)
        })
    }
  }, [book, bookId, history, role?.name])

  // Run tut on first collaborator login.
  useEffect(() => {
    if (!role) {
      return
    }
    const { name, is_tour_shown, role_id } = role

    if (name !== 'Purchaser' && !is_tour_shown) {
      axiosClient.put(`/role/${role_id}/tour`)
      launchTour()
    }
  }, [book, bookId, launchTour, role])

  const pendingStoryList =
    questions.length > 0 && questions.filter((q) => q.is_sent === 0)
  const pendingStoryCount =
    pendingStoryList && pendingStoryList.length > 0
      ? pendingStoryList.length
      : 0

  const completedStoryList =
    questions.length > 0 &&
    questions.filter((_q) => {
      let _answers = _q.answers
      return _q.is_sent && _answers.filter((_a) => _a.is_submitted).length
    })

  let completedStoryCount =
    completedStoryList && completedStoryList.length > 0
      ? completedStoryList.length
      : 0

  const startedStoryList =
    questions.length > 0 &&
    questions.filter((_q) => {
      let _answers = _q.answers
      return (
        _q.is_sent &&
        (_answers.filter((_a) => parseInt(_a.is_submitted) === 0).length ||
          _answers.length === 0)
      )
    })

  const startedStoryCount =
    startedStoryList && startedStoryList.length > 0
      ? startedStoryList.length
      : 0

  let totalCount = completedStoryCount + startedStoryCount
  if (isEditorOrPurchaser) {
    totalCount += pendingStoryCount
  }

  let completedBarWidth = '0%'
  let pendingBarWidth = '0%'
  if (totalCount > 0) {
    completedBarWidth = `${(100 * completedStoryCount) / totalCount}%`
    if (isEditorOrPurchaser) {
      pendingBarWidth = `${(100 * pendingStoryCount) / totalCount}%`
    }
  }

  const BarColor = styled.div`
    .color-bar {
      height: 2em;
      background-color: #ffcc00;
      background-repeat: repeat-x;
    }

    .color-bar:before {
      height: 2em;
      width: ${completedBarWidth};
      display: block;
      content: '';
      float: left;
      background-color: #4e9e69;
    }

    .color-bar:after {
      height: 2em;
      width: ${pendingBarWidth};
      display: block;
      content: '';
      float: right;
      background-color: #ff4d4f;
    }

    .color-bar-gray {
      height: 2em;
      background-color: #f2f2f2;
      background-repeat: repeat-x;
    }
  `

  return (
    <BookInfoContainer id="info-section">
      <div className="title-section section-container">
        {book?.id && stats ? (
          <div className="prompts-progress">
            <div className="prompts-bar-container">
              {completedStoryCount + startedStoryCount + pendingStoryCount >
              0 ? (
                <BarColor>
                  <div className="color-bar"></div>
                </BarColor>
              ) : (
                <BarColor>
                  <div className="color-bar-gray"></div>
                </BarColor>
              )}
            </div>
            <div className="prompt-stats">
              <div>
                <StyledTooltip
                  title="Prompts with all responses completed."
                  placement="top"
                >
                  <div className="prompt-state-label">Complete</div>
                  <h2 style={{ color: '#4E9E69' }}>{completedStoryCount}</h2>
                </StyledTooltip>
              </div>
              <div className="pl40">
                <StyledTooltip
                  title="Prompts that have been sent out, but aren't yet finished."
                  placement="top"
                >
                  <div className="prompt-state-label">Started</div>
                  <h2 style={{ color: '#FFCC00' }}>{startedStoryCount}</h2>
                </StyledTooltip>
              </div>
              {isEditorOrPurchaser && (
                <div className="pl40">
                  <StyledTooltip
                    title="Prompts that haven't yet been sent."
                    placement="top"
                  >
                    <div className="prompt-state-label">Pending</div>
                    <h2 style={{ color: '#FF4D4F' }}>{pendingStoryCount}</h2>
                  </StyledTooltip>
                </div>
              )}
            </div>
          </div>
        ) : (
          <Skeleton.Input active block style={{ height: 80 }} />
        )}
      </div>
      {book?.id ? (
        <div className="project-stats">
          <div className="info-item">
            <span className="material-symbols-outlined stat-icon">
              menu_book
            </span>
            <h4>
              <span className="stat-count">{stats?.prompt_count}</span>
              <StyledTooltip
                title="Prompts that haven't yet been sent."
                placement="bottom"
              >
                <span className="stat-label">{t('story_prompts')}</span>
              </StyledTooltip>
            </h4>
          </div>
          <div className="info-item">
            <MdOutlineAutoStories className="stat-icon" />
            <h4>
              <span className="stat-count">
                {stats?.estimated_page_count || 0}
              </span>
              <StyledTooltip
                title="The total number of pages that your book is expected to contain."
                placement="bottom"
              >
                <span className="stat-label">{t('estimate_pages')}</span>
              </StyledTooltip>
            </h4>
          </div>
          <div className="info-item">
            <MdOutlineQuiz className="stat-icon" />
            <h4>
              <span className="stat-count">{stats?.story_count}</span>
              <StyledTooltip
                title="The total number of completed responses."
                placement="bottom"
              >
                <span className="stat-label">{t('stories_shared')}</span>
              </StyledTooltip>
            </h4>
          </div>
          <div className="info-item">
            <MdOutlineMarkEmailRead className="stat-icon" />
            <h4>
              <span className="stat-count">{stats?.image_count}</span>
              <StyledTooltip
                title="The number of photos in your book."
                placement="bottom"
              >
                <span className="stat-label">{t('number_of_photos')}</span>
              </StyledTooltip>
            </h4>
          </div>
        </div>
      ) : (
        <div className="section-container">
          <Skeleton.Input active block />
        </div>
      )}

      <div className="info-item">
        <ThingsToDo />
      </div>
      <div className="info-item">
        <RecentActivity />
      </div>
    </BookInfoContainer>
  )
}

export default BookInfo
